// News.jsx - Página de noticias para FeelLab (Con modal)
import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { 
  FaNewspaper, 
  FaCalendarAlt, 
  FaUser, 
  FaTag, 
  FaBookOpen, 
  FaExternalLinkAlt,
  FaHeart,
  FaPaw,
  FaLeaf,
  FaGraduationCap,
  FaSearch,
  FaFilter,
  FaPlus,
  FaClock,
  FaSpinner,
  FaTimes
} from 'react-icons/fa';

const News = () => {
  const [selectedCategory, setSelectedCategory] = useState('todas');
  const [searchTerm, setSearchTerm] = useState('');
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estados para modal
  const [selectedNews, setSelectedNews] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Cargar noticias y categorías al montar
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      
      const newsResponse = await api.getNews();
      const categoriesResponse = await api.getCategories('news');
      
      // Estructura correcta: response.data.items
      const newsData = newsResponse?.data?.items || [];
      const categoriesData = Array.isArray(categoriesResponse) 
        ? categoriesResponse 
        : (categoriesResponse?.data?.items || categoriesResponse?.data || []);
      
      setNews(newsData);
      
      const allCategories = [
        { id: 'todas', name: 'Todas', icon: 'FaNewspaper', color: 'from-sky-400 to-emerald-400' },
        ...(Array.isArray(categoriesData) ? categoriesData.map(cat => ({
          ...cat,
          icon: getIconForCategory(cat.slug),
          color: cat.color || 'from-emerald-400 to-teal-400'
        })) : [])
      ];
      setCategories(allCategories);
      
    } catch (err) {
      console.error('Error cargando datos:', err);
      setError('No se pudieron cargar las noticias');
    } finally {
      setLoading(false);
    }
  };

  // Mapear iconos según categoría
  const getIconForCategory = (slug) => {
    const iconMap = {
      'programa': 'FaGraduationCap',
      'investigacion': 'FaBookOpen',
      'educacion': 'FaGraduationCap',
      'alianza': 'FaHeart'
    };
    return iconMap[slug] || 'FaNewspaper';
  };

  // Mapear string de icono a componente
  const getIconComponent = (iconName) => {
    const icons = {
      FaNewspaper,
      FaGraduationCap,
      FaBookOpen,
      FaHeart
    };
    return icons[iconName] || FaNewspaper;
  };

  // Abrir modal
  const openNewsDetail = (newsItem) => {
    setSelectedNews(newsItem);
    setShowModal(true);
    document.body.style.overflow = 'hidden'; // Bloquear scroll del body
  };

  // Cerrar modal
  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
    document.body.style.overflow = 'unset'; // Restaurar scroll
  };

  // Lecturas útiles (permanecen hardcodeadas)
  const usefulReadings = [
    {
      id: 1,
      title: "Educación para el Bienestar Animal (EBA): estrategias pedagógicas para promover empatía por los animales",
      authors: ["Enrique Chaux", "Leydy Escobar", "Sergio Almeida", "Sara Quiceno"],
      source: "ResearchGate",
      url: "https://www.researchgate.net/publication/376576753_Educacion_para_el_Bienestar_Animal_EBA_estrategias_pedagogicas_para_promover_empatia_por_los_animales",
      description: "Estrategias pedagógicas innovadoras para desarrollar empatía hacia los animales en entornos educativos.",
      type: "PDF",
      category: "pedagogia"
    },
    {
      id: 2,
      title: "Creating Compassion: Using Art for Empathy Learning with Urban Youth",
      authors: ["Laura J. Morizio", "Amy L. Cook", "Rebecca Troeger", "Anna Whitehouse"],
      source: "Contemporary School Psychology - SpringerLink",
      url: "https://link.springer.com/article/10.1007/s40688-020-00346-1",
      description: "Investigación sobre el uso del arte como herramienta para desarrollar empatía en jóvenes urbanos.",
      type: "Artículo",
      category: "arte"
    },
    {
      id: 3,
      title: "Enseñanza-aprendizaje del bienestar animal: mejores estrategias en Latinoamérica",
      authors: ["World Animal Protection", "Fischer", "Pereira Bengoa", "Gebara", "Gamboa"],
      source: "ResearchGate",
      url: "https://www.researchgate.net/publication/301498164_Mejores_estrategias_practicas_y_actividades_docentes_para_la_ensenanza_efectiva_del_bienestar_animal_en_Latinoamerica",
      description: "Estudio completo sobre las mejores prácticas educativas en bienestar animal para América Latina.",
      type: "PDF",
      category: "investigacion"
    },
    {
      id: 4,
      title: "Política Nacional de Protección y Bienestar Animal",
      authors: ["Ministerio de Ambiente de Colombia"],
      source: "Ministerio de Ambiente",
      url: "https://www.minambiente.gov.co/wp-content/uploads/2025/05/VF_PoliticaNacionalProteccionBienestarAnimal_7_5_25.pdf",
      description: "Marco legal y político oficial para la protección y bienestar animal en Colombia.",
      type: "Documento oficial",
      category: "politica"
    },
    {
      id: 5,
      title: "Propuesta pedagógica en protección y bienestar animal",
      authors: ["Secretaría de Educación de Bucaramanga (SEB)"],
      source: "Secretaría de Educación de Bogotá",
      url: "https://www.seb.gov.co/wp-content/uploads/2022/06/DT-PROTECCI%C3%93N-ANIMAL-2022.pdf",
      description: "Documento con propuestas pedagógicas para implementar educación en protección animal en colegios.",
      type: "PDF",
      category: "educacion"
    },
    {
      id: 6,
      title: "Implementación de una estrategia educativa: tenencia responsable de mascotas",
      authors: ["YS Enciso Sierra"],
      source: "Repositorio Libertadores",
      url: "https://repository.libertadores.edu.co/bitstreams/fe4cfc74-7dfd-4d2d-87f7-a8ff7ebf855c/download",
      description: "Trabajo práctico sobre estrategias educativas para promover la tenencia responsable de mascotas.",
      type: "Tesis",
      category: "practica"
    }
  ];

  const readingCategories = [
    { id: 'pedagogia', name: 'Pedagogía', color: 'emerald' },
    { id: 'arte', name: 'Arte', color: 'purple' },
    { id: 'investigacion', name: 'Investigación', color: 'blue' },
    { id: 'politica', name: 'Política', color: 'red' },
    { id: 'educacion', name: 'Educación', color: 'yellow' },
    { id: 'practica', name: 'Práctica', color: 'green' }
  ];

  // Filtrar noticias
  const filteredNews = (Array.isArray(news) ? news : []).filter(item => {
    const matchesCategory = selectedCategory === 'todas' || item.category_id === selectedCategory;
    const matchesSearch = item.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (item.excerpt && item.excerpt.toLowerCase().includes(searchTerm.toLowerCase()));
    return matchesCategory && matchesSearch;
  });

  const getCategoryColor = (category) => {
    const colors = {
      pedagogia: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      arte: 'bg-purple-100 text-purple-700 border-purple-200',
      investigacion: 'bg-blue-100 text-blue-700 border-blue-200',
      politica: 'bg-red-100 text-red-700 border-red-200',
      educacion: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      practica: 'bg-green-100 text-green-700 border-green-200'
    };
    return colors[category] || 'bg-gray-100 text-gray-700 border-gray-200';
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('es-ES', options);
  };

  // Estado de carga
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 flex items-center justify-center">
        <div className="text-center">
          <FaSpinner className="text-6xl text-emerald-500 animate-spin mx-auto mb-4" />
          <p className="text-emerald-700 font-medium">Cargando noticias...</p>
        </div>
      </div>
    );
  }

  // Estado de error
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 flex items-center justify-center">
        <div className="text-center bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl max-w-md">
          <FaNewspaper className="text-6xl text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-red-700 mb-2">Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={loadData}
            className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 py-2 rounded-full font-bold hover:shadow-lg transition-all"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-sky-50 py-16 relative overflow-hidden">
      {/* Elementos decorativos de fondo */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 text-6xl text-emerald-500 animate-bounce">📰</div>
        <div className="absolute top-40 right-20 text-4xl text-teal-500 animate-pulse">📚</div>
        <div className="absolute bottom-32 left-20 text-5xl text-sky-500 animate-bounce delay-300">🔬</div>
        <div className="absolute bottom-20 right-32 text-3xl text-emerald-600 animate-pulse delay-700">✨</div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-3 bg-white/80 rounded-full px-8 py-4 backdrop-blur-sm mb-6 shadow-lg border border-emerald-100">
            <FaPaw className="text-emerald-600 text-xl" />
            <span className="font-medium text-emerald-800">Centro de Noticias</span>
            <FaPaw className="text-emerald-600 text-xl" />
          </div>
          
          <h1 className="text-5xl md:text-6xl font-titulo font-bold mb-6 bg-gradient-to-r from-emerald-600 via-teal-600 to-sky-600 bg-clip-text text-transparent">
            Noticias FeelLab
          </h1>
          
          <p className="text-xl text-emerald-700 max-w-3xl mx-auto leading-relaxed">
            Mantente al día con las últimas investigaciones, programas y noticias 
            sobre educación para el bienestar animal.
          </p>
        </div>

        {/* Barra de búsqueda */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <FaSearch className="absolute left-6 top-1/2 -translate-y-1/2 text-emerald-500 text-xl" />
            <input
              type="text"
              placeholder="Buscar noticias..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-16 pr-6 py-5 rounded-full bg-white/80 backdrop-blur-sm shadow-lg border-2 border-emerald-100 focus:border-emerald-400 focus:outline-none text-emerald-900 placeholder-emerald-400 text-lg transition-all duration-300"
            />
          </div>
        </div>

        {/* Filtro de categorías */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => {
            const IconComponent = getIconComponent(category.icon);
            const isActive = selectedCategory === category.id;
            const newsCount = category.id === 'todas' 
              ? (Array.isArray(news) ? news.length : 0)
              : (Array.isArray(news) ? news.filter(n => n.category_id === category.id).length : 0);

            return (
              <button
                key={category.id}
                onClick={() => setSelectedCategory(category.id)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-full font-bold transition-all duration-300 transform hover:scale-105 shadow-lg ${
                  isActive
                    ? `bg-gradient-to-r ${category.color} text-white`
                    : 'bg-white/80 backdrop-blur-sm text-emerald-700 hover:bg-emerald-50'
                }`}
              >
                <IconComponent className={`text-lg ${isActive ? 'text-white' : 'text-emerald-600'}`} />
                <span>{category.name}</span>
                <span className={`text-sm px-2 py-1 rounded-full font-bold ${
                  isActive ? 'bg-white/30 text-white' : 'bg-emerald-100 text-emerald-700'
                }`}>
                  {newsCount}
                </span>
              </button>
            );
          })}
        </div>

        {/* Grid de noticias principales */}
        <div className="mb-16">
          <h2 className="text-3xl font-bold text-emerald-800 mb-8 text-center">
            Últimas Noticias
          </h2>
          
          {filteredNews.length === 0 ? (
            <div className="text-center py-16">
              <FaNewspaper className="text-6xl text-emerald-300 mx-auto mb-4" />
              <p className="text-emerald-600 text-xl">No se encontraron noticias</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredNews.map((newsItem) => (
                <article
                  key={newsItem.id}
                  className="group bg-white/80 backdrop-blur-sm rounded-3xl shadow-xl hover:shadow-2xl transform hover:-translate-y-3 transition-all duration-500 overflow-hidden border border-emerald-100/50 hover:border-emerald-200 flex flex-col h-[580px]"
                >
                  {/* Imagen - Altura fija */}
                  {newsItem.image_url ? (
                    <div className="h-48 overflow-hidden relative flex-shrink-0">
                      <img 
                        src={newsItem.image_url} 
                        alt={newsItem.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      {newsItem.is_featured && (
                        <div className="absolute top-3 left-3 bg-amber-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                          Destacada
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="bg-gradient-to-br from-emerald-100 to-teal-100 h-48 flex items-center justify-center relative overflow-hidden group-hover:scale-110 transition-transform duration-500 flex-shrink-0">
                      <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      <div className="text-center relative z-10">
                        <FaNewspaper className="text-emerald-500 text-4xl mb-2 mx-auto" />
                        <p className="text-emerald-700 text-sm">Imagen de la noticia</p>
                      </div>
                      {newsItem.is_featured && (
                        <div className="absolute top-3 left-3 bg-amber-400 text-white px-3 py-1 rounded-full text-xs font-bold">
                          Destacada
                        </div>
                      )}
                    </div>
                  )}

                  {/* Contenido - Flex grow para ocupar espacio restante */}
                  <div className="p-6 flex flex-col flex-grow">
                    {/* Metadatos */}
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center space-x-3 text-sm text-emerald-600">
                        <FaCalendarAlt />
                        <span>{formatDate(newsItem.published_at || newsItem.created_at)}</span>
                      </div>
                      {newsItem.read_time && (
                        <div className="flex items-center space-x-2 text-sm text-emerald-600">
                          <FaClock />
                          <span>{newsItem.read_time}</span>
                        </div>
                      )}
                    </div>

                    {/* Título - Altura fija con line-clamp */}
                    <h3 className="font-bold text-xl text-emerald-900 group-hover:text-teal-700 transition-colors mb-3 line-clamp-2 h-14">
                      {newsItem.title}
                    </h3>

                    {/* Extracto - Altura fija con line-clamp */}
                    <p className="text-emerald-700 text-sm mb-4 leading-relaxed line-clamp-3 h-16 flex-grow">
                      {newsItem.excerpt}
                    </p>

                    {/* Autor y categoría */}
                    <div className="flex items-center justify-between mb-4">
                      {newsItem.author_name && (
                        <div className="flex items-center space-x-2 text-xs text-emerald-600">
                          <FaUser />
                          <span className="line-clamp-1">{newsItem.author_name}</span>
                        </div>
                      )}
                      <span className="text-xs bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full font-medium border border-emerald-200 capitalize">
                        {newsItem.category_name || 'General'}
                      </span>
                    </div>

                    {/* Botón leer más */}
                    <button 
                      onClick={() => openNewsDetail(newsItem)}
                      className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-full font-bold text-sm hover:shadow-lg transform hover:scale-105 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <FaBookOpen />
                      <span>Leer más</span>
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>

        {/* Sección de lecturas útiles */}
        <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-emerald-100">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-emerald-800 mb-4 flex items-center justify-center space-x-3">
              <FaBookOpen className="text-emerald-600" />
              <span>Lecturas Útiles de Investigación</span>
            </h2>
            <p className="text-emerald-700 max-w-2xl mx-auto">
              Recursos académicos y documentos de referencia para profundizar en 
              educación para el bienestar animal y desarrollo de empatía.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {usefulReadings.map((reading) => (
              <div
                key={reading.id}
                className="bg-white/90 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-emerald-100 hover:border-emerald-200"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-2">
                    <span className={`text-xs px-3 py-1 rounded-full font-medium border ${getCategoryColor(reading.category)}`}>
                      {readingCategories.find(cat => cat.id === reading.category)?.name || reading.category}
                    </span>
                    <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full">
                      {reading.type}
                    </span>
                  </div>
                </div>

                {/* Título */}
                <h3 className="font-bold text-lg text-emerald-900 mb-3 leading-tight">
                  {reading.title}
                </h3>

                {/* Autores */}
                <div className="flex items-center space-x-2 mb-3 text-sm text-emerald-600">
                  <FaUser />
                  <span className="line-clamp-1">{reading.authors.join(', ')}</span>
                </div>

                {/* Descripción */}
                <p className="text-emerald-700 text-sm mb-4 leading-relaxed">
                  {reading.description}
                </p>

                {/* Fuente y enlace */}
                <div className="flex items-center justify-between">
                  <span className="text-xs text-emerald-600 font-medium">
                    Fuente: {reading.source}
                  </span>
                  <a
                    href={reading.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center space-x-2 bg-emerald-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-emerald-600 transform hover:scale-105 transition-all duration-300"
                  >
                    <span>Acceder</span>
                    <FaExternalLinkAlt />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center mt-16">
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 text-white shadow-xl">
            <h3 className="text-2xl font-bold mb-4">¿Tienes una noticia que compartir?</h3>
            <p className="text-emerald-100 mb-6 max-w-2xl mx-auto">
              Comparte tus experiencias, investigaciones o iniciativas relacionadas 
              con la educación en empatía animal.
            </p>
            <button className="bg-white text-emerald-600 px-8 py-3 rounded-full font-bold hover:shadow-xl transform hover:scale-105 transition-all duration-300">
              Enviar Noticia
            </button>
          </div>
        </div>
      </div>

      {/* Modal de detalle de noticia */}
      {showModal && selectedNews && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header del modal con imagen */}
            <div className="relative">
              {selectedNews.image_url ? (
                <div className="h-64 overflow-hidden">
                  <img 
                    src={selectedNews.image_url} 
                    alt={selectedNews.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                </div>
              ) : (
                <div className="h-64 bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center">
                  <FaNewspaper className="text-emerald-500 text-6xl" />
                </div>
              )}
              
              {/* Botón cerrar */}
              <button
                onClick={closeModal}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white text-emerald-700 rounded-full p-3 shadow-lg transition-all transform hover:scale-110"
              >
                <FaTimes className="text-xl" />
              </button>
              
              {selectedNews.is_featured && (
                <div className="absolute top-4 left-4 bg-amber-400 text-white px-4 py-2 rounded-full text-sm font-bold">
                  Destacada
                </div>
              )}
            </div>

            {/* Contenido del modal - scrollable */}
            <div className="flex-1 overflow-y-auto p-8">
              {/* Categoría */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm bg-emerald-100 text-emerald-700 px-4 py-2 rounded-full font-medium border border-emerald-200 capitalize">
                  {selectedNews.category_name || 'General'}
                </span>
                {selectedNews.read_time && (
                  <div className="flex items-center space-x-2 text-emerald-600">
                    <FaClock />
                    <span className="text-sm">{selectedNews.read_time}</span>
                  </div>
                )}
              </div>

              {/* Título */}
              <h2 className="text-4xl font-bold text-emerald-900 mb-4">
                {selectedNews.title}
              </h2>

              {/* Metadata */}
              <div className="flex items-center space-x-6 text-emerald-600 mb-6 pb-6 border-b border-emerald-100">
                <div className="flex items-center space-x-2">
                  <FaCalendarAlt />
                  <span className="text-sm">{formatDate(selectedNews.published_at || selectedNews.created_at)}</span>
                </div>
                {selectedNews.author_name && (
                  <div className="flex items-center space-x-2">
                    <FaUser />
                    <span className="text-sm">{selectedNews.author_name}</span>
                  </div>
                )}
                <div className="flex items-center space-x-2">
                  <FaNewspaper />
                  <span className="text-sm">{selectedNews.views || 0} vistas</span>
                </div>
              </div>

              {/* Contenido completo */}
              <div 
                className="prose prose-emerald max-w-none text-emerald-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: selectedNews.content }}
              />
            </div>

            {/* Footer del modal */}
            <div className="border-t border-emerald-100 p-6 bg-emerald-50/50">
              <button
                onClick={closeModal}
                className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white py-3 rounded-full font-bold hover:shadow-lg transform hover:scale-105 transition-all duration-300"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default News;
